import React, { useEffect, useMemo, useState } from 'react'
import { apiClient } from '../lib/api'
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_PaginationState, type MRT_SortingState, type MRT_ColumnFiltersState, type MRT_ColumnSizingState, type MRT_ColumnPinningState } from 'material-react-table'
import { getDefaultMRTOptions } from '../table-utils/mrtDefaults'
import { Checkbox, FormControlLabel, Tabs, Tab, Box, TextField, Button, Typography, Paper, Stack, useTheme } from '@mui/material'
import { useTableStatePersistence } from '../hooks/useTableStatePersistence'

export const BIC: React.FC = () => {
  const theme = useTheme()
  
  // Initialize table state persistence
  const { state: tableState, updateState, isLoaded } = useTableStatePersistence({
    reportId: 'bic',
    localStoragePrefix: 'bic',
    defaultState: {
      globalFilter: '',
      columnOrder: ['ro_number', 'customer_first', 'customer_last', 'type_code', 't1_label', 't1_confidence', 't1_method', 't1_version', 't2_label', 't2_confidence', 't2_method', 't2_version', 't2_created_at'],
      columnVisibility: {},
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 50 },
      sorting: [{ id: 't2_created_at', desc: true }],
      columnFilters: [],
      columnSizing: {},
      columnPinning: { left: ['ro_number'], right: [] }
    }
  })
  
  // Extract state values for easier access
  const {
    globalFilter = '',
    columnOrder = [],
    columnVisibility = {},
    density = 'compact',
    pagination = { pageIndex: 0, pageSize: 50 },
    sorting = [],
    columnFilters = [],
    columnSizing = {},
    columnPinning = { left: ['ro_number'], right: [] }
  } = tableState as any
  
  // Local state for data and UI
  const [rows, setRows] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [triggering, setTriggering] = useState(false)
  const [tab, setTab] = useState<'results' | 'config'>('results')

  // Config/state
  const [batchLimit, setBatchLimit] = useState<number>(200)
  const [savingConfig, setSavingConfig] = useState(false)
  const [status, setStatus] = useState<any | null>(null)
  const [runningFullClassification, setRunningFullClassification] = useState(false)
  const [fullClassificationProgress, setFullClassificationProgress] = useState<string>('')

  const page = (pagination?.pageIndex ?? 0) + 1
  const pageSize = pagination?.pageSize ?? 50

  const t1FilterValues = useMemo(() => {
    const f = columnFilters.find((filter: any) => filter.id === 't1_label')
    const val = (f?.value ?? []) as any
    if (Array.isArray(val)) return val as string[]
    if (typeof val === 'string' && val) return [val]
    return []
  }, [columnFilters])

  const qs = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(page))
    p.set('pageSize', String(pageSize))
    if (globalFilter.trim()) p.set('name', globalFilter.trim())
    if (t1FilterValues.length) p.set('t1Label', t1FilterValues.join(','))
    return p.toString()
  }, [page, pageSize, globalFilter, t1FilterValues])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    apiClient.get(`/api/reports/bic/latest?${qs}`)
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(d => { if (!cancelled) { setRows(d.rows || []); setTotal(d.total || 0) } })
      .catch(e => { if (!cancelled) setError(String(e?.message || e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [qs])

  // Fetch config + status when config tab opens
  useEffect(() => {
    let cancelled = false
    if (tab !== 'config') return
    ;(async () => {
      try {
        const [cfgResp, stResp] = await Promise.all([
          apiClient.get('/api/bic/config'),
          apiClient.get('/api/bic/status'),
        ])
        if (cfgResp.ok) {
          const d = await cfgResp.json()
          const v = Number(d?.config?.batchLimit ?? 200)
          if (!cancelled && isFinite(v)) setBatchLimit(v)
        }
        if (stResp.ok) {
          const s = await stResp.json()
          if (!cancelled) setStatus(s)
        }
      } catch (e) {
        // ignore
      }
    })()
    return () => { cancelled = true }
  }, [tab])

  async function triggerBIC(tier: 1 | 2) {
    setTriggering(true)
    setError(null)
    try {
      const resp = await apiClient.post('/api/bic/trigger', { tier, limit: 200 })
      if (!resp.ok) throw new Error(`Trigger failed: ${resp.status}`)
      const out = await resp.json()
      if (!out.ok) throw new Error(out.error || 'Trigger failed')
      // Refresh list after trigger completes
      updateState('pagination', { ...pagination, pageIndex: 0 })
      const r = await apiClient.get(`/api/reports/bic/latest?${new URLSearchParams({ page: '1', pageSize: String(pageSize), ...(globalFilter.trim() ? { name: globalFilter.trim() } : {}), ...(t1FilterValues.length ? { t1Label: t1FilterValues.join(',') } : {}) }).toString()}`)
      if (r.ok) {
        const d = await r.json()
        setRows(d.rows || [])
        setTotal(d.total || 0)
      }
      // Refresh status when on config tab
      if (tab === 'config') {
        const s = await apiClient.get('/api/bic/status')
        if (s.ok) setStatus(await s.json())
      }
    } catch (e: any) {
      setError(String(e?.message || e))
    } finally {
      setTriggering(false)
    }
  }

  async function saveConfig() {
    setSavingConfig(true)
    try {
      const resp = await apiClient.put('/api/bic/config', { batchLimit: batchLimit })
      if (!resp.ok) throw new Error(`Save failed: ${resp.status}`)
      const out = await resp.json()
      if (!out.ok) throw new Error(out.error || 'Save failed')
    } catch (e) {
      // surface minimal error in UI header
      setError(String((e as any)?.message || e))
    } finally {
      setSavingConfig(false)
    }
  }

  async function runFullClassification() {
    setRunningFullClassification(true)
    setError(null)
    setFullClassificationProgress('')
    
    try {
      let totalProcessed = 0
      let batchCount = 0
      const batchSize = 200 // Fixed batch size for full classification
      
      while (true) {
        batchCount++
        setFullClassificationProgress(`Running batch ${batchCount} (${totalProcessed} processed so far)...`)
        
        // Trigger one batch
        const resp = await apiClient.post('/api/bic/trigger', { tier: 1, limit: batchSize })
        if (!resp.ok) throw new Error(`Batch ${batchCount} failed: ${resp.status}`)
        
        const result = await resp.json()
        if (!result.ok) throw new Error(result.error || `Batch ${batchCount} failed`)
        
        // Get updated status to check progress
        const statusResp = await apiClient.get('/api/bic/status')
        if (statusResp.ok) {
          const newStatus = await statusResp.json()
          setStatus(newStatus)
          
          // Check if we have unclassified records remaining
          if (newStatus.unclassifiedCount <= 0) {
            setFullClassificationProgress(`Complete! Processed ${totalProcessed} records total in ${batchCount} batches.`)
            break
          }
          
          totalProcessed += batchSize
          setFullClassificationProgress(`Batch ${batchCount} complete. ${newStatus.unclassifiedCount} records remaining...`)
          
          // Small delay between batches to avoid overwhelming the service
          await new Promise(resolve => setTimeout(resolve, 1000))
        } else {
          throw new Error('Failed to get status after batch')
        }
      }
      
      // Refresh the results table if we're on that tab
      if (tab === 'results') {
        updateState('pagination', { ...pagination, pageIndex: 0 })
        const r = await apiClient.get(`/api/reports/bic/latest?${new URLSearchParams({ page: '1', pageSize: String(pageSize), ...(globalFilter.trim() ? { name: globalFilter.trim() } : {}), ...(t1FilterValues.length ? { t1Label: t1FilterValues.join(',') } : {}) }).toString()}`)
        if (r.ok) {
          const d = await r.json()
          setRows(d.rows || [])
          setTotal(d.total || 0)
        }
      }
      
    } catch (e: any) {
      setError(String(e?.message || e))
      setFullClassificationProgress(`Failed: ${e?.message || e}`)
    } finally {
      setRunningFullClassification(false)
    }
  }

  // Reset page index when filters change (not on pagination change)
  useEffect(() => {
    updateState('pagination', { ...pagination, pageIndex: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(columnFilters), globalFilter, t1FilterValues.join(','), updateState])

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
    { accessorKey: 'ro_number', header: 'RO Number', size: 120, enableColumnFilter: false, enableClickToCopy: true },
    { accessorKey: 'customer_first', header: 'First Name', size: 160 },
    { 
      accessorKey: 'customer_last', 
      header: 'Last Name', 
      size: 160,
      Cell: ({ cell }: any) => (
        <Box 
          component="span" 
          sx={{ 
            color: theme.palette.mode === 'dark' ? '#FFFFFF !important' : '#111111 !important',
            fontWeight: 'inherit'
          }}
        >
          {String(cell.getValue?.() ?? '')}
        </Box>
      )
    },
    { accessorKey: 'type_code', header: 'Type Code', size: 110, enableColumnFilter: true, filterVariant: 'multi-select', filterSelectOptions: [
      { text: 'Company (C)', value: 'C' },
      { text: 'Individual (I)', value: 'I' },
      { text: '(blank)', value: '' },
    ] },
    {
      accessorKey: 't1_label',
      header: 'Tier 1',
      size: 160,
      enableColumnFilter: true,
      filterVariant: 'multi-select', // MRT Autocomplete with checkbox list
      filterSelectOptions: [
        { text: 'Business', value: 'business' },
        { text: 'Individual', value: 'individual' },
        { text: 'Internal', value: 'internal' },
        { text: 'Voided', value: 'voided' },
      ],
    },
    { accessorKey: 't1_confidence', header: 'T1 Confidence', size: 140, Cell: ({ cell }) => {
      const v = cell.getValue<number | null>()
      return v != null ? Number(v).toFixed(2) : ''
    } },
    { accessorKey: 't1_method', header: 'T1 Method', size: 140 },
    { accessorKey: 't1_version', header: 'T1 Version', size: 140 },
    { accessorKey: 't2_label', header: 'Tier 2', size: 140 },
    { accessorKey: 't2_confidence', header: 'T2 Confidence', size: 140, Cell: ({ cell }) => {
      const v = cell.getValue<number | null>()
      return v != null ? Number(v).toFixed(2) : ''
    } },
    { accessorKey: 't2_method', header: 'T2 Method', size: 140 },
    { accessorKey: 't2_version', header: 'T2 Version', size: 140 },
    { accessorKey: 't2_created_at', header: 'Last Classified', size: 200, Cell: ({ row }) => {
      const t2 = row.original.t2_created_at
      const t1 = row.original.t1_created_at
      const lastAt = t2 || t1
      return lastAt ? new Date(lastAt).toLocaleString() : ''
    } },
  ], [theme])

  const table = useMaterialReactTable({
    ...getDefaultMRTOptions<any>(),
    columns,
    data: rows,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    rowCount: total,
    enableColumnDragging: true,
    enableSorting: true,
    enableColumnResizing: true,
    enableColumnPinning: true,
    state: {
      isLoading: loading,
      pagination,
      globalFilter,
      sorting,
      columnFilters,
      columnVisibility,
      density,
      columnOrder,
      columnSizing,
      columnPinning,
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater
      updateState('pagination', next)
    },
    onGlobalFilterChange: (updater) => {
      const next = typeof updater === 'function' ? updater(globalFilter) : updater
      updateState('globalFilter', next)
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      updateState('sorting', next)
    },
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater
      updateState('columnFilters', next)
    },
    onColumnVisibilityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnVisibility) : updater
      updateState('columnVisibility', next)
    },
    onDensityChange: (updater) => {
      const next = typeof updater === 'function' ? updater(density) : updater
      updateState('density', next)
    },
    onColumnOrderChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnOrder) : updater
      updateState('columnOrder', next)
    },
    onColumnSizingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnSizing) : updater
      updateState('columnSizing', next)
    },
    onColumnPinningChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnPinning) : updater
      updateState('columnPinning', next)
    },
    initialState: {
      density,
      columnVisibility,
      sorting,
      columnOrder: columnOrder.length ? columnOrder : undefined,
      columnSizing,
      columnPinning,
    },
    muiSearchTextFieldProps: {
      ...getDefaultMRTOptions<any>().muiSearchTextFieldProps,
      placeholder: 'Search (name:)',
    } as any,
  })

  return (
    <Box sx={{ p: 2 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Results" value="results" />
        <Tab label="Configs" value="config" />
      </Tabs>

      {tab === 'results' && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button variant="contained" disabled={triggering} onClick={() => triggerBIC(1)}> {triggering ? 'Running…' : 'Run Tier 1'} </Button>
            <Button variant="outlined" disabled={triggering} onClick={() => triggerBIC(2)}> {triggering ? 'Running…' : 'Run Tier 2'} </Button>
            <Box sx={{ ml: 'auto' }}>Total: {total}</Box>
          </Stack>
          {error && <Typography color="error" sx={{ mb: 1 }}>Error: {error}</Typography>}
          <MaterialReactTable table={table} />
        </Box>
      )}

      {tab === 'config' && (
        <Box sx={{ mt: 2 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Batch Settings</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                label="Batch Limit"
                type="number"
                value={batchLimit}
                inputProps={{ min: 1, max: 1000 }}
                onChange={(e) => setBatchLimit(Math.max(1, Math.min(1000, Number(e.target.value) || 0)))}
              />
              <Button variant="contained" disabled={savingConfig} onClick={saveConfig}>Save</Button>
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>Server enforces 1..1000 and uses this as default for new runs.</Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Full Classification</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Run classification on all remaining records in batches of 200 until complete.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Button 
                variant="contained" 
                color="primary"
                disabled={runningFullClassification || (status?.unclassifiedCount <= 0)}
                onClick={runFullClassification}
                sx={{ minWidth: 200 }}
              >
                {runningFullClassification ? 'Running Full Classification...' : 
                 status?.unclassifiedCount <= 0 ? 'All Records Classified' :
                 `Classify All (${status?.unclassifiedCount ?? '—'} remaining)`}
              </Button>
            </Stack>
            {fullClassificationProgress && (
              <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace' }}>
                {fullClassificationProgress}
              </Typography>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Current Status</Typography>
            <Typography variant="body2">Total Records: {status?.totalRecords ?? '—'}</Typography>
            <Typography variant="body2">Classified (any tier): {status?.classifiedAny ?? '—'}</Typography>
            <Typography variant="body2">Finalized: {status?.finalCount ?? '—'}</Typography>
            <Typography variant="body2">Unclassified: {status?.unclassifiedCount ?? '—'}</Typography>
            {Array.isArray(status?.breakdownFinal) && status.breakdownFinal.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">Final Breakdown</Typography>
                {status.breakdownFinal.map((b: any, i: number) => (
                  <Typography key={i} variant="body2">{b.label}: {b.count}</Typography>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  )
}
